import { Box, TextField } from "@mui/material";
import { useEffect, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";

import BooleanConditionValueField from "./BooleanConditionValueField";
import { ChoiceFieldComponent } from "../ChoiceField";
import { DateFieldComponent } from "../DateField";
import { DatetimeFieldComponent } from "../DatetimeField";
import { TimeFieldComponent } from "../TimeField";
import { NumberFieldComponent } from "../NumberField";
import { CurrencyFieldComponent } from "../CurrencyField";
import { ReferenceFieldComponent } from "../ReferenceField";
import { TableNameFieldComponent } from "../TableNameField";
import { TextFieldComponent } from "../TextField";
import { DynamicValueField } from "./DynamicValueField";
import { InValueField } from "./InValueField";
import { TemporalConditionValueField } from "./TemporalConditionValueField";

import SysColumn from "app/class/sys-column";
import { shouldUseDynamicField } from "./utils/operators.config";

const FIELD_COMPONENT_MAP: Record<string, any> = {
    UUID: TextFieldComponent,
    BOOLEAN: BooleanConditionValueField,
    CHOICE: ChoiceFieldComponent,
    DATE: DateFieldComponent,
    DATETIME: DatetimeFieldComponent,
    TIME: TimeFieldComponent,
    INTEGER: NumberFieldComponent,
    DECIMAL: NumberFieldComponent,
    CURRENCY: CurrencyFieldComponent,
    CODE: TextFieldComponent,
    RICH_HTML: TextFieldComponent,
    REFERENCE: ReferenceFieldComponent,
    DYNAMIC_REFERENCE: TextFieldComponent,
    PERMISSION_ARRAY: TextFieldComponent,
    TABLE_NAME: TableNameFieldComponent,
    TEXT: TextFieldComponent,
    TRANSLATABLE_TEXT: TextFieldComponent,
};
const sameValue = (left: unknown, right: unknown) => Object.is(left, right)
    || (typeof left === "object" && typeof right === "object" && JSON.stringify(left) === JSON.stringify(right));

export interface ValueFieldProps {
    column: SysColumn | undefined;
    value: any;
    onChange: (value: any) => void;
    disabled?: boolean;
    operator?: string;
    columns?: SysColumn[];
    allColumns?: SysColumn[];
    tables?: any[];
    maxReferenceDepth?: number;
    formFields?: { name: string; label: string }[];
    ariaLabel?: string;
    placeholder?: string;
}

export const ValueField = ({
    column,
    value,
    onChange,
    disabled,
    operator,
    columns,
    allColumns,
    tables,
    maxReferenceDepth,
    formFields,
    ariaLabel,
    placeholder,
}: ValueFieldProps) => {
    const shouldRenderDynamicField = Boolean(operator && shouldUseDynamicField(operator, value));
    const onChangeRef = useRef(onChange);
    const valueContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const input = valueContainerRef.current?.querySelector("input, textarea");
        if (input && ariaLabel) input.setAttribute("aria-label", ariaLabel);
        if (input && placeholder) input.setAttribute("placeholder", placeholder);
    }, [ariaLabel, placeholder, column?.column_type, operator]);
    
    /**
     * RHF ISOLADO
     */
    const form = useForm({
        defaultValues: { value },
        shouldUnregister: false,
        mode: "onChange",
    });

    /**
     * Evita loop AST <-> RHF
     */
    const lastExternalValue = useRef(value);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    /**
     * AST -> RHF (hidratação / mudanças externas)
     */
    useEffect(() => {
        if (!sameValue(lastExternalValue.current, value)) {
            lastExternalValue.current = value;
            form.reset({ value });
        }
    }, [value, form]);

    useEffect(() => {
        const subscription = form.watch((values, { name }) => {
            if (name && name !== "value") {
                return;
            }

            const nextValue = values.value;

            if (!sameValue(nextValue, lastExternalValue.current)) {
                onChangeRef.current(nextValue);
                lastExternalValue.current = nextValue;
            }
        });

        return () => {
            subscription.unsubscribe();
        }
    }, [form]);

    if (!column) {
        return <TextField size="small" sx={{ minWidth: 140, width: "100%" }} inputProps={{ "aria-label": ariaLabel }} placeholder={placeholder} disabled value="" />;
    }

    if (operator === "in") {
        return (
            <Box sx={{ width: "100%" }}>
                <InValueField
                    column={column}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                />
            </Box>
        );
    }

    // Verifica se deve usar campo dinâmico baseado no operador OU no valor
    if (shouldRenderDynamicField) {
        return (
            <Box sx={{ width: "100%" }}>
                <DynamicValueField
                    value={value}
                    columns={columns || []} // Usa as colunas da tabela do ConditionBuilderField
                    allColumns={allColumns}
                    tables={tables}
                    maxReferenceDepth={maxReferenceDepth}
                    formFields={formFields}
                    selectedFieldType={column.column_type}
                    onChange={onChange}
                />
            </Box>
        );
    }

    if (column.column_type === "DATE" || column.column_type === "DATETIME") {
        return (
            <TemporalConditionValueField
                columnType={column.column_type}
                operator={operator}
                value={value}
                onChange={onChange}
                disabled={disabled}
            />
        );
    }

    const Component = FIELD_COMPONENT_MAP[column.column_type];
    if (!Component) {
        return <TextField size="small" sx={{ minWidth: 140, width: "100%" }} inputProps={{ "aria-label": ariaLabel }} placeholder={placeholder} disabled value="" />;
    }

    return (
        <Box ref={valueContainerRef} sx={{ width: "100%" }}>
            <FormProvider {...form}>
                <Component
                    key={`${column.column_type}:${column.id}:${operator || "default"}`}
                    column={column}
                    control={form.control}
                    baseFieldProps={{
                        removeHeader: true
                    }}
                />
            </FormProvider>
        </Box>
    );
};
